import React, { ComponentType, CSSProperties, useCallback } from "react";

import cn from "classnames";
import { Draggable, Droppable } from "react-beautiful-dnd";

import Identified from "@mapmycustomers/shared/types/base/Identified";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import BoardColumn from "../types/BoardColumn";
import ColumnFooterProps from "../types/ColumnFooterProps";
import ColumnHeaderProps from "../types/ColumnHeaderProps";
import ItemComponentProps from "../types/ItemComponentProps";

import styles from "./Column.module.scss";
import ColumnHeader from "./ColumnHeader";
import HiddenColumn from "./HiddenColumn";

const LOAD_CARD_THRESHOLD = 200;

interface Props<DataType, ItemType extends Identified> {
  analyticIssuer?: AnalyticsService;
  column: BoardColumn<DataType, ItemType>;
  columnFooter?: ComponentType<ColumnFooterProps<DataType, ItemType>>;
  columnSubHeader?: ComponentType<ColumnHeaderProps<DataType, ItemType>>;
  dragDestination?: boolean;
  draggingInProgress?: boolean;
  draggingItemId?: ItemType["id"];
  dragSource?: boolean;
  getColumnStyles?: (
    column: BoardColumn<DataType, ItemType>,
    props: { dragging?: boolean; isDestination?: boolean; isSource?: boolean }
  ) => CSSProperties | undefined;
  getItemListStyles?: (
    column: BoardColumn<DataType, ItemType>,
    props: { dragging?: boolean; isDestination?: boolean; isSource?: boolean }
  ) => CSSProperties | undefined;
  hidden?: boolean;
  itemComponent: ComponentType<ItemComponentProps<ItemType>>;
  onHide?: (column: BoardColumn<DataType, ItemType>) => void;
  onHover?: (column: BoardColumn<DataType, ItemType>) => void;
  onLoadMore?: (data: DataType) => void;
  onShow?: (column: BoardColumn<DataType, ItemType>) => void;
}

const Column = <DataType, ItemType extends Identified>({
  analyticIssuer,
  column,
  columnFooter: ColumnFooter,
  columnSubHeader,
  dragDestination,
  draggingInProgress,
  draggingItemId,
  dragSource,
  getColumnStyles,
  getItemListStyles,
  hidden,
  itemComponent: ItemComponent,
  onHide,
  onHover,
  onLoadMore,
  onShow,
}: Props<DataType, ItemType>) => {
  const handleMouseEnter = useCallback(() => onHover?.(column), [column, onHover]);

  const handleScroll = useCallback(
    ({ target }) => {
      const { clientHeight, scrollHeight, scrollTop } = target;
      if (
        scrollHeight - clientHeight - scrollTop < LOAD_CARD_THRESHOLD &&
        !column.loading &&
        column.items.length <= (column?.total ?? 0)
      ) {
        onLoadMore?.(column.data);
      }
    },
    [column, onLoadMore]
  );

  return (
    <Droppable droppableId={String(column.id)} key={column.id}>
      {(provided) => (
        <div
          className={cn(styles.container, { [styles.hidden]: hidden })}
          onMouseEnter={handleMouseEnter}
          ref={provided.innerRef}
          style={getColumnStyles?.(column, {
            dragging: draggingInProgress,
            isDestination: dragDestination,
            isSource: dragSource,
          })}
          {...provided.droppableProps}
        >
          {hidden ? (
            <HiddenColumn column={column} onShow={onShow} />
          ) : (
            <ColumnHeader column={column} columnSubHeader={columnSubHeader} onHide={onHide} />
          )}

          <div
            className={styles.items}
            onScroll={handleScroll}
            style={getItemListStyles?.(column, {
              dragging: draggingInProgress,
              isDestination: dragDestination,
              isSource: dragSource,
            })}
          >
            {provided.placeholder}
            {(!hidden && (!draggingInProgress || dragSource) ? column.items : []).map(
              (item, index) => (
                <Draggable draggableId={String(item.id)} index={index} key={item.id}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <ItemComponent dragging={item.id === draggingItemId} item={item} />
                    </div>
                  )}
                </Draggable>
              )
            )}
            {column.loading && <LoadingSpinner />}
            {ColumnFooter && !hidden && !draggingInProgress && (
              <ColumnFooter analyticIssuer={analyticIssuer} column={column} />
            )}
          </div>
        </div>
      )}
    </Droppable>
  );
};

export default Column;
